import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ROUTE_ORDER_SUMMARY } from "../../constants/routes";
import type { Plan } from "../../Graphql/types/plan";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import PlanCardView from "./PlanCardView";
import { FeatureCode } from "../Features/enums/featureEnums";
import AnimatedContainer from "../common/AnimatedContainer";
import { useGetPlanList, useGetPlanPricing } from "../../hooks/api/usePlan";
import { useGetBillingCycleList } from "../../hooks/api/useBillingCycle";
import type { RootState } from "../../store/store";

interface PlanWithFeatures extends Plan {
  price?: number;
  currency?: string;
  billingCycle?: string;
  features?: Array<{
    featureCode: FeatureCode;
    name: string;
    included: boolean;
  }>;
}

interface ResolvedPricing {
  priceAmount: number;
  priceCurrency: string;
  billingCycleLabel: string;
}

const PlanListView: React.FC = () => {
  const navigate = useNavigate();
  const { planList } = useSelector((state: RootState) => state.plans);
  const getPlanListMutation = useGetPlanList();
  const getBillingCycleListMutation = useGetBillingCycleList();
  const getPlanPricingMutation = useGetPlanPricing();

  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [selectedBillingCycleId, setSelectedBillingCycleId] = useState<
    string | null
  >(null);
  const [planPricingByPlanId, setPlanPricingByPlanId] = useState<
    Record<string, ResolvedPricing>
  >({});

  useEffect(() => {
    getPlanListMutation.mutateAsync({ activeOnly: true }).catch(() => {});
    getBillingCycleListMutation
      .mutateAsync({ activeOnly: true })
      .then((res) => {
        const list = res.listBillingCycles.billingCycleList ?? [];
        setBillingCycles(list);
        if (list.length > 0) {
          const oneMonth = list.find(
            (bc) => bc.durationValue === 1 && bc.durationUnit === "MONTH",
          );
          setSelectedBillingCycleId(
            (id) => id ?? oneMonth?.billingCycleId ?? list[0].billingCycleId,
          );
        }
      })
      .catch(() => {});
  }, []);

  const activePlansFromStore = useMemo(
    () => (planList ?? []).filter((p) => p.isActive),
    [planList],
  );

  useEffect(() => {
    if (
      activePlansFromStore.length === 0 ||
      !selectedBillingCycleId ||
      billingCycles.length === 0
    ) {
      setPlanPricingByPlanId({});
      return;
    }
    const selectedCycle = billingCycles.find(
      (bc) => bc.billingCycleId === selectedBillingCycleId,
    );
    const label = selectedCycle?.label;
    const cycleIdAtStart = selectedBillingCycleId;

    const run = async () => {
      const next: Record<string, ResolvedPricing> = {};
      for (const plan of activePlansFromStore) {
        try {
          const res = await getPlanPricingMutation.mutateAsync({
            planId: plan.planId,
          });
          const list = res.getPlanPricing?.planPricingList ?? [];
          const entry = list.find(
            (p) => p.billingCycleId === cycleIdAtStart && p.isActive,
          );
          if (entry) {
            next[plan.planId] = {
              priceAmount: entry.priceAmount,
              priceCurrency: entry.priceCurrency,
              billingCycleLabel: label || "",
            };
          }
        } catch {
          // per-plan errors handled by fetchGraphQL/setAlert
        }
      }
      setPlanPricingByPlanId((prev) => {
        if (cycleIdAtStart !== selectedBillingCycleId) return prev;
        return Object.keys(next).length > 0 ? next : prev;
      });
    };
    run();
  }, [activePlansFromStore, selectedBillingCycleId, billingCycles]);

  const plans: PlanWithFeatures[] = (planList ?? []).map((p) => {
    const pricing = planPricingByPlanId[p.planId];
    return {
      ...p,
      description: p.description,
      price: pricing?.priceAmount,
      currency: pricing?.priceCurrency,
      billingCycle: pricing?.billingCycleLabel,
      features: [],
    };
  });

  const handleSelectPlan = (planId: string) => {
    const selectedPlan = plans.find((p) => p.planId === planId);
    if (selectedPlan) {
      const pricing = planPricingByPlanId[planId];
      const selectedCycle = billingCycles.find(
        (bc) => bc.billingCycleId === selectedBillingCycleId,
      );
      navigate(ROUTE_ORDER_SUMMARY, {
        state: {
          plan: {
            planId: selectedPlan.planId,
            name: selectedPlan.name,
            price: pricing?.priceAmount ?? selectedPlan.price ?? 0,
            currency: pricing?.priceCurrency ?? selectedPlan.currency ?? "RWF",
            billingCycle:
              pricing?.billingCycleLabel ??
              selectedPlan.billingCycle ??
              "Per month",
            billingCycleId: selectedBillingCycleId ?? undefined,
            durationValue: selectedCycle?.durationValue,
            durationUnit: selectedCycle?.durationUnit,
            description: selectedPlan.description,
          },
        },
      });
    }
  };

  const colorSequence: Array<"blue" | "purple" | "pink"> = [
    "blue",
    "purple",
    "pink",
  ];
  const activePlans = plans.filter((plan) => plan.isActive);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        boxSizing: "border-box",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)",
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)",
            },
          },
        }}
      />
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <AnimatedContainer
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: {
                  xs: "1.25rem",
                  sm: "1.60rem",
                  md: "2.65rem",
                  lg: "2.75rem",
                },
                lineHeight: 1.3,
                mb: 3,
                color: "white",
                letterSpacing: { xs: "0.01em", md: "0.02em" },
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              }}
            >
              Choose your plan
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                lineHeight: 1.7,
                maxWidth: 600,
                mx: "auto",
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: 400,
              }}
            >
              Select the perfect plan for your business needs. All plans include
              a 7-day free trial.
            </Typography>
          </AnimatedContainer>
        </Box>

        {/* Billing cycle selection */}
        {billingCycles.length > 0 && (
          <AnimatedContainer
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{
                    color: "rgba(255, 255, 255, 0.95)",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  Billing cycle
                </FormLabel>
                <RadioGroup
                  row
                  value={selectedBillingCycleId ?? ""}
                  onChange={(e) =>
                    setSelectedBillingCycleId(e.target.value || null)
                  }
                  sx={{
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  {billingCycles
                    .filter((bc) => bc.isActive)
                    .map((cycle) => (
                      <FormControlLabel
                        key={cycle.billingCycleId}
                        value={cycle.billingCycleId}
                        control={
                          <Radio
                            sx={{
                              color: "white",
                              "&.Mui-checked": {
                                color: "white",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                                color: "white",
                              },
                              "& .MuiSvgIcon-root": {
                                color: "inherit",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: "rgba(255, 255, 255, 0.95)",
                              fontSize: "0.95rem",
                            }}
                          >
                            {cycle.label}
                          </Typography>
                        }
                        sx={{ mr: 2 }}
                      />
                    ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </AnimatedContainer>
        )}

        {/* Plan Cards */}
        {activePlans.length > 0 && (
          <Box
            sx={{
              mb: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Grid
              container
              spacing={4}
              justifyContent="center"
              sx={{ width: "100%" }}
            >
              {activePlans.map((plan, index) => {
                return (
                  // @ts-expect-error - Grid item prop is valid, TypeScript inference issue with MUI v7
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={4}
                    xl={4}
                    key={plan.planId}
                    sx={{
                      display: "flex",
                      width: "100%",
                      maxWidth: {
                        xs: "100%",
                        sm: "100%",
                        md: "100%",
                        lg: "400px",
                      },
                    }}
                  >
                    <PlanCardView
                      plan={plan}
                      price={plan.price}
                      currency={plan.currency}
                      billingCycle={plan.billingCycle}
                      features={plan.features}
                      onSelect={handleSelectPlan}
                      isPopular={plan.name.includes("Premium")} // Middle card is popular (only if multiple plans)
                      color={colorSequence[index % colorSequence.length]}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PlanListView;
